import { createFileRoute, Link } from "@tanstack/react-router";
import { SERVICE_AREAS } from "@/data/service-areas";

const TITLE = "Service Areas | Hulsey Creative Co. — Web Design Near Haleyville, AL";
const DESCRIPTION =
  "Local website design and SEO for businesses across north Alabama and northeast Mississippi — 40+ towns within 100 miles of our Haleyville, AL studio.";
const URL = "https://hulseycreativeco.com/service-area";

export const Route = createFileRoute("/service-area/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: ServiceAreaIndex,
});

function ServiceAreaIndex() {
  const al = SERVICE_AREAS.filter((s) => s.stateAbbr === "AL");
  const ms = SERVICE_AREAS.filter((s) => s.stateAbbr === "MS");
  return (
    <div className="theme-tech">
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-6 pt-12 pb-12 md:px-8 md:pt-20 md:pb-16">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent-2 mb-6">
            /service-area · 100-mile radius
          </p>
          <h1 className="font-display text-4xl uppercase tracking-tight leading-[0.95] text-[color:var(--tech-text)] md:text-6xl">
            Where we work, <span className="text-highlight">in plain English</span>.
          </h1>
          <p className="mt-8 max-w-[60ch] text-base leading-relaxed text-[color:var(--tech-text)]/80 md:text-lg">
            Hulsey Creative Co. is based in Haleyville, Alabama. These are the {SERVICE_AREAS.length} towns
            we serve directly from that office — every one is within about 100 miles of our front door.
            Each page below is written specifically for that community, not a template.
          </p>
          <div className="mt-8">
            <Link
              to="/service-area/county"
              className="font-mono text-xs uppercase tracking-widest text-accent-2 hover:text-highlight"
            >
              Browse by county (125-mile radius) →
            </Link>
          </div>
          <div className="mt-3">
            <Link
              to="/service-area/state"
              className="font-mono text-xs uppercase tracking-widest text-accent-2 hover:text-highlight"
            >
              Browse by state →
            </Link>
          </div>
        </div>
      </section>

      <StateSection label="Alabama" count={al.length} heading="North Alabama service area." cities={al} bordered />
      <StateSection label="Mississippi" count={ms.length} heading="Northeast Mississippi service area." cities={ms} />
    </div>
  );
}

function StateSection({
  label,
  count,
  heading,
  cities,
  bordered,
}: {
  label: string;
  count: number;
  heading: string;
  cities: typeof SERVICE_AREAS;
  bordered?: boolean;
}) {
  return (
    <section className={bordered ? "border-b border-line" : undefined}>
      <div className="mx-auto max-w-7xl px-6 py-14 md:px-8 md:py-20">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent-2 mb-3">
          {label} · {count} towns
        </p>
        <h2 className="font-display text-2xl uppercase tracking-tight text-[color:var(--tech-text)] md:text-4xl">
          {heading}
        </h2>
        <ul className="mt-10 grid gap-px bg-[color:var(--tech-line)] border border-line sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {cities.map((s) => (
            <li key={s.slug} className="bg-[color:var(--tech-panel)]">
              <Link
                to="/service-area/$city"
                params={{ city: s.slug }}
                className="flex flex-col gap-1 p-5 transition-colors hover:bg-[color:var(--tech-panel-2)]"
              >
                <span className="text-base text-[color:var(--tech-text)]">
                  {s.city}, {s.stateAbbr}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                  {s.county} · ~{s.driveMinutes}m
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}